import { apiClient } from '../../../services/apiService';
import { deleteRequest, getRequest, postRequest } from '../../../services/apiService';
import {
  AddMembersResponse,
  AdminUserDTO,
  AnalyticsOverview,
  CampaignLaunchBody,
  CampaignSummary,
  CohortBucket,
  CreateCustomSegmentBody,
  CustomSegmentMembersResponse,
  SegmentMembersResponse,
  CombineSegmentsBody,
  CombineSegmentsPreview,
  CombineSegmentsResult,
  CustomSegmentSummary,
  DailyPoint,
  UpdateCustomSegmentBody,
  EmailSendSettingsSnapshot,
  EmailSendSettingsUpdate,
  EmailUsageResponse,
  InsightsPayload,
  LeadListFilters,
  LeadListResponse,
  LeadSummary,
  LiveNow,
  LiveUsersResponse,
  LookupUsersByEmailResponse,
  MutationError,
  RoleValue,
  SystemNotificationResult,
  SegmentDescription,
  SegmentPreview,
  TierBucket,
  UserListFilters,
  UserListResponse,
} from '../types';

const BASE = '/api/v1/admin/dashboard';

/** Unwrap an axios response or pass through if the helper already gave us data. */
function unwrap<T>(r: any): T | null {
  if (r == null) return null;
  if (r.data !== undefined) return r.data as T;
  return r as T;
}

function withCacheBust(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_=${Date.now()}`;
}

async function getFresh<T>(url: string): Promise<T | null> {
  const response = await apiClient.get(withCacheBust(url), {
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
    },
  });
  return response.data as T;
}

export async function fetchOverview(): Promise<AnalyticsOverview | null> {
  return unwrap<AnalyticsOverview>(await getRequest(`${BASE}/analytics/overview`));
}

export async function fetchSignupsByDay(days = 30): Promise<DailyPoint[]> {
  return unwrap<DailyPoint[]>(await getRequest(`${BASE}/analytics/signups?days=${days}`)) ?? [];
}

export async function fetchActiveByDay(days = 30): Promise<DailyPoint[]> {
  return unwrap<DailyPoint[]>(await getRequest(`${BASE}/analytics/active-users?days=${days}`)) ?? [];
}

export async function fetchTierDistribution(): Promise<TierBucket[]> {
  return unwrap<TierBucket[]>(await getRequest(`${BASE}/analytics/tier-distribution`)) ?? [];
}

export async function fetchCohortRetention(weeks = 8, retentionWindowDays = 14): Promise<CohortBucket[]> {
  return unwrap<CohortBucket[]>(
    await getRequest(`${BASE}/analytics/cohort-retention?weeks=${weeks}&retentionWindowDays=${retentionWindowDays}`)
  ) ?? [];
}

export async function fetchRecentCampaigns(limit = 20): Promise<CampaignSummary[]> {
  return unwrap<CampaignSummary[]>(await getRequest(`${BASE}/analytics/campaigns?limit=${limit}`)) ?? [];
}

export async function fetchUsers(filters: UserListFilters): Promise<UserListResponse | null> {
  const params = new URLSearchParams();
  if (filters.role) params.set('role', filters.role);
  if (filters.signedUpAfter) params.set('signedUpAfter', filters.signedUpAfter);
  if (filters.signedUpBefore) params.set('signedUpBefore', filters.signedUpBefore);
  if (filters.activeAfter) params.set('activeAfter', filters.activeAfter);
  if (filters.suppressed !== undefined) params.set('suppressed', String(filters.suppressed));
  if (filters.q) params.set('q', filters.q);
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.size !== undefined) params.set('size', String(filters.size));
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.dir) params.set('dir', filters.dir);
  const qs = params.toString();
  return await getFresh<UserListResponse>(`${BASE}/users${qs ? `?${qs}` : ''}`);
}

export async function fetchRecentSignups(days = 7, size = 50): Promise<UserListResponse | null> {
  return await getFresh<UserListResponse>(`${BASE}/users/recent-signups?days=${days}&size=${size}`);
}

// ─── Lead capture (hero form / popup / pre-signup email collection) ───

export async function fetchLeads(filters: LeadListFilters): Promise<LeadListResponse | null> {
  const params = new URLSearchParams();
  if (filters.q) params.set('q', filters.q);
  if (filters.source) params.set('source', filters.source);
  if (filters.createdAfter) params.set('createdAfter', filters.createdAfter);
  if (filters.converted !== undefined) params.set('converted', String(filters.converted));
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.size !== undefined) params.set('size', String(filters.size));
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.dir) params.set('dir', filters.dir);
  const qs = params.toString();
  return await getFresh<LeadListResponse>(`${BASE}/leads${qs ? `?${qs}` : ''}`);
}

export async function fetchLeadsSummary(): Promise<LeadSummary | null> {
  return await getFresh<LeadSummary>(`${BASE}/leads/summary`);
}

export async function fetchUser(id: number): Promise<AdminUserDTO | null> {
  return unwrap<AdminUserDTO>(await getRequest(`${BASE}/users/${id}`));
}

export async function setUserMarketingSuppression(
  id: number,
  suppressed: boolean,
  reason = 'MANUAL',
): Promise<AdminUserDTO> {
  const result = await postRequest(`${BASE}/users/${id}/marketing-suppression`, { suppressed, reason });
  if (!result) throw new Error('No response from marketing suppression API');
  return result as AdminUserDTO;
}

export type ResendBounceImportResult = {
  pagesScanned: number;
  emailsScanned: number;
  bouncedFound: number;
  complainedFound: number;
  knownSendsUpdated: number;
  newlySuppressedUsers: number;
  skippedNoRecipient: number;
  note?: string;
};

export async function importResendBounces(): Promise<ResendBounceImportResult> {
  try {
    const response = await apiClient.post(`${BASE}/admin-jobs/import-resend-bounces`, {});
    return response.data as ResendBounceImportResult;
  } catch (e) {
    const data = (e as { response?: { data?: { message?: string } } })?.response?.data;
    if (data?.message) {
      throw new Error(data.message);
    }
    throw new Error('Resend bounce import failed — check connection or admin permissions.');
  }
}

export async function fetchUserSegments(id: number): Promise<SegmentDescription[]> {
  return await getFresh<SegmentDescription[]>(`${BASE}/users/${id}/segments`) ?? [];
}

export async function lookupUsersByEmail(emails: string[]): Promise<LookupUsersByEmailResponse | null> {
  const unique = Array.from(new Set(emails.map((e) => e.trim().toLowerCase()).filter(Boolean)));
  if (unique.length === 0) return { matched: [], unmatched: [] };
  const params = new URLSearchParams({ emails: unique.join(',') });
  return unwrap<LookupUsersByEmailResponse>(await getRequest(`${BASE}/users/lookup?${params.toString()}`));
}

export async function fetchSegments(): Promise<SegmentDescription[]> {
  return await getFresh<SegmentDescription[]>(`${BASE}/segments`) ?? [];
}

export async function previewSegment(key: string, limit = 20): Promise<SegmentPreview | null> {
  return await getFresh<SegmentPreview>(`${BASE}/segments/${encodeURIComponent(key)}/preview?limit=${limit}`);
}

export async function launchSegmentCampaign(
  key: string,
  body: CampaignLaunchBody
): Promise<CampaignSummary | null> {
  const result = await postRequest(`${BASE}/segments/${encodeURIComponent(key)}/campaign`, body);
  if (!result) return null;
  return result as CampaignSummary;
}

/**
 * Fetch the live-activity counts. Sends {@code todaySinceMinutes} so
 * the backend can anchor "today" to the caller's local midnight (the
 * default UTC anchor mismatches the calendar day every admin sees
 * outside UTC, see {@link
 * com.bookieburglar.api.services.controllers.AdminDashboardController#liveNow}).
 */
export async function fetchLiveNow(): Promise<LiveNow | null> {
  const minutesSinceLocalMidnight = (() => {
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.max(1, Math.floor((now.getTime() - midnight.getTime()) / 60000));
  })();
  return unwrap<LiveNow>(
    await getRequest(
      `${BASE}/analytics/live-now?todaySinceMinutes=${minutesSinceLocalMidnight}`,
    ),
  );
}

export async function fetchLiveUsers(minutes = 5, limit = 50): Promise<LiveUsersResponse | null> {
  return unwrap<LiveUsersResponse>(
    await getRequest(`${BASE}/analytics/live-users?minutes=${minutes}&limit=${limit}`)
  );
}

export async function fetchInsights(): Promise<InsightsPayload | null> {
  return unwrap<InsightsPayload>(await getRequest(`${BASE}/analytics/insights`));
}

// ─── Custom segments ──────────────────────────────────────────────────

export async function createCustomSegment(body: CreateCustomSegmentBody): Promise<CustomSegmentSummary | null> {
  const result = await postRequest(`${BASE}/segments/custom`, body);
  if (!result) return null;
  return result as CustomSegmentSummary;
}

export async function deleteCustomSegment(id: number): Promise<boolean> {
  const r = await deleteRequest(`${BASE}/segments/custom/${id}`);
  return r !== null;
}

/**
 * Update a custom segment's label and/or description. The slug is
 * deliberately immutable — past campaigns reference it for attribution
 * — so this endpoint cannot rename it. Returns the new {@link CustomSegmentSummary}
 * shape on success and null on error / not-found.
 */
export async function updateCustomSegment(
  id: number,
  body: UpdateCustomSegmentBody,
): Promise<CustomSegmentSummary | null> {
  try {
    const res = await apiClient.patch(`${BASE}/segments/custom/${id}`, body);
    return res.data as CustomSegmentSummary;
  } catch {
    return null;
  }
}

/**
 * Pure-stats preview of what {@link combineCustomSegments} would do
 * for the given source keys. The combine modal calls this when the
 * user picks segments / changes selection so it can render
 * "1,247 unique users · 52 cold-lead emails skipped" before
 * committing.
 */
export async function previewCombineSegments(
  sourceKeys: string[],
): Promise<CombineSegmentsPreview | null> {
  try {
    const res = await apiClient.post(`${BASE}/segments/custom/combine/preview`, { sourceKeys });
    return res.data as CombineSegmentsPreview;
  } catch {
    return null;
  }
}

/**
 * Combine two-or-more segments (preset and/or custom) into a new
 * custom list of unique users. Returns null on validation error
 * (e.g. unknown segment, empty union); the caller decides whether to
 * surface the underlying message — for that, use
 * {@link previewCombineSegments} first.
 */
export async function combineCustomSegments(
  body: CombineSegmentsBody,
): Promise<CombineSegmentsResult | null> {
  try {
    const res = await apiClient.post(`${BASE}/segments/custom/combine`, body);
    return res.data as CombineSegmentsResult;
  } catch {
    return null;
  }
}

export async function addCustomSegmentMembers(id: number, userIds: number[]): Promise<AddMembersResponse | null> {
  const result = await postRequest(`${BASE}/segments/custom/${id}/members`, { userIds });
  if (!result) return null;
  return result as AddMembersResponse;
}

export async function removeCustomSegmentMember(id: number, userId: number): Promise<boolean> {
  const r = await deleteRequest(`${BASE}/segments/custom/${id}/members/${userId}`);
  return r !== null;
}

export async function fetchCustomSegmentMembers(id: number, limit = 100): Promise<CustomSegmentMembersResponse | null> {
  return await getFresh<CustomSegmentMembersResponse>(`${BASE}/segments/custom/${id}/members?limit=${limit}`);
}

/**
 * Hydrate a segment (preset or custom) to its first {@code limit}
 * members for the admin "view members" modal. Use this instead of
 * {@link fetchCustomSegmentMembers} for any new code; the latter is
 * kept around because the {@code Remove} action on the modal needs
 * the numeric custom-segment id, and only custom segments support
 * removal.
 */
export async function fetchSegmentMembers(key: string, limit = 200): Promise<SegmentMembersResponse | null> {
  return await getFresh<SegmentMembersResponse>(`${BASE}/segments/${encodeURIComponent(key)}/members?limit=${limit}`);
}

export async function fetchUserCustomSegments(userId: number): Promise<number[]> {
  return unwrap<number[]>(await getRequest(`${BASE}/users/${userId}/custom-segments`)) ?? [];
}

// ─── Email send settings ──────────────────────────────────────────────

export async function fetchEmailSettings(): Promise<EmailSendSettingsSnapshot | null> {
  return unwrap<EmailSendSettingsSnapshot>(await getRequest(`${BASE}/email-settings`));
}

export async function updateEmailSettings(body: EmailSendSettingsUpdate): Promise<EmailSendSettingsSnapshot | null> {
  // PUT — apiService doesn't have a generic putRequest that returns response.data,
  // so call the underlying axios client directly so we get the error path
  // surfaced by the global response interceptor (auth refresh, etc.).
  try {
    const res = await apiClient.put(`${BASE}/email-settings`, body);
    return res.data as EmailSendSettingsSnapshot;
  } catch (e: any) {
    // Surface the failure in console so silent-swallow doesn't hide the
    // root cause when an admin action looks like a no-op. The caller can
    // still treat null as "failed" but at least there's a breadcrumb.
    const status = e?.response?.status;
    const detail = e?.response?.data ?? e?.message ?? e;
    console.error('updateEmailSettings failed', { status, body, detail });
    return null;
  }
}

export async function fetchEmailUsage(days = 30): Promise<EmailUsageResponse | null> {
  return unwrap<EmailUsageResponse>(await getRequest(`${BASE}/email-settings/usage?days=${days}`));
}

// ─── User access mutations ────────────────────────────────────────────

export type UserMutationResult =
  | { ok: true; user: AdminUserDTO }
  | { ok: false; error: MutationError };

/**
 * Both setUserRole and setUserAccess return the updated user on
 * success and a typed {@link MutationError} on a 4xx (e.g. self_demote,
 * last_admin). Keeping the discriminated union here so the UI can
 * handle "you tried to demote yourself" without inspecting axios
 * internals.
 */
export async function setUserRole(userId: number, role: RoleValue): Promise<UserMutationResult> {
  try {
    const res = await apiClient.post(`${BASE}/users/${userId}/role`, { role });
    return { ok: true, user: res.data as AdminUserDTO };
  } catch (e) {
    return parseMutationError(e);
  }
}

export async function setUserAccess(
  userId: number,
  revoked: boolean,
  reason?: string,
): Promise<UserMutationResult> {
  try {
    const res = await apiClient.post(`${BASE}/users/${userId}/access`, { revoked, reason });
    return { ok: true, user: res.data as AdminUserDTO };
  } catch (e) {
    return parseMutationError(e);
  }
}

/**
 * Sign a user out everywhere — revoke all of their active JWTs while
 * leaving their access intact. They'll have to log in again on next
 * request, but won't be blocked from doing so.
 */
export async function signOutUserEverywhere(userId: number): Promise<UserMutationResult> {
  try {
    const res = await apiClient.post(`${BASE}/users/${userId}/sign-out`, {});
    return { ok: true, user: res.data as AdminUserDTO };
  } catch (e) {
    return parseMutationError(e);
  }
}

/**
 * Force a fresh fetch of this user's Stripe subscription state, then
 * return the updated DTO. Costs ~one Stripe round-trip.
 */
export async function refreshUserStripe(userId: number): Promise<UserMutationResult> {
  try {
    const res = await apiClient.post(`${BASE}/users/${userId}/refresh-stripe`, {});
    return { ok: true, user: res.data as AdminUserDTO };
  } catch (e) {
    return parseMutationError(e);
  }
}

/**
 * Refresh every user's cached Stripe subscription state. Long-running
 * (one Stripe round-trip per user, throttled to ~10/sec). Returns the
 * count of users we attempted to refresh.
 */
export async function refreshAllStripe(): Promise<number> {
  const res = await apiClient.post(`${BASE}/admin-jobs/refresh-stripe-all`, {});
  return (res.data as { refreshed?: number })?.refreshed ?? 0;
}

/**
 * Re-run the {@code created_at} / {@code last_active_at} backfill over
 * any rows that still have null timestamps. Idempotent.
 */
export async function backfillTimestamps(): Promise<number> {
  const res = await apiClient.post(`${BASE}/admin-jobs/backfill-timestamps`, {});
  return (res.data as { updated?: number })?.updated ?? 0;
}

// ─── On-the-fly notifications ─────────────────────────────────────────

/** Hard ceiling matched by SystemNotificationService.MAX_MESSAGE_LENGTH on the backend. */
export const SYSTEM_NOTIFICATION_MAX_LEN = 280;

/**
 * Send an in-app notification to a single user from the
 * notifications@bookiebrawler.com system account (the same actor that
 * posts the daily free arb of the day). Validation errors are returned
 * as the {@link SystemNotificationResult} discriminated union so callers
 * don't have to inspect axios internals.
 */
export async function sendSystemNotification(
  userId: number,
  message: string,
): Promise<SystemNotificationResult> {
  try {
    const res = await apiClient.post(`${BASE}/users/${userId}/notify`, { message });
    return { ok: true, sent: 1, missing: 0, ...(res.data as object) } as SystemNotificationResult;
  } catch (e) {
    return parseSystemNotificationError(e);
  }
}

export async function broadcastSystemNotification(
  userIds: number[],
  message: string,
): Promise<SystemNotificationResult> {
  try {
    const res = await apiClient.post(`${BASE}/notifications/broadcast`, { userIds, message });
    return { ok: true, sent: 0, missing: 0, ...(res.data as object) } as SystemNotificationResult;
  } catch (e) {
    return parseSystemNotificationError(e);
  }
}

/**
 * Segment-targeted variant — sends a SYSTEM notification to every user
 * in the given preset or custom segment. Backend resolves the segment
 * to user IDs (cold-leads and email-only entries are dropped in the
 * resolver), then runs the same {@code sendToUsers} flow as
 * {@link broadcastSystemNotification}. No warm-up or daily-cap
 * pacing — system notifications are transactional / operational, not
 * marketing.
 *
 * <p>Mirrors {@link launchSegmentCampaign}'s shape so the UI flow can
 * stay symmetrical: launch-campaign vs notify-segment, both keyed on
 * the same {@code key} from {@code /admin/segments}.
 */
export async function notifySegment(
  key: string,
  message: string,
): Promise<SystemNotificationResult> {
  try {
    const res = await apiClient.post(
      `${BASE}/segments/${encodeURIComponent(key)}/notify`,
      { message },
    );
    return { ok: true, sent: 0, missing: 0, ...(res.data as object) } as SystemNotificationResult;
  } catch (e) {
    return parseSystemNotificationError(e);
  }
}

// ─── Live cohort (Overview "Active now") ──────────────────────────────
// These two helpers target the in-memory "active in the last N minutes"
// cohort directly rather than going through a persisted segment. Used
// by the Overview "Active now" pill so an admin can fire a heads-up
// notification or an email blast at whoever is on the site right now
// without first saving a segment. Backend echoes a synthetic
// {@code segmentKey} of {@code "live:Nm"} on the response so the UI
// post-send banner reads naturally for both segments and live cohorts.

export async function notifyLiveUsers(
  windowMinutes: number,
  message: string,
): Promise<SystemNotificationResult> {
  try {
    const res = await apiClient.post(
      `${BASE}/analytics/live-users/notify?minutes=${windowMinutes}`,
      { message },
    );
    return { ok: true, sent: 0, missing: 0, ...(res.data as object) } as SystemNotificationResult;
  } catch (e) {
    return parseSystemNotificationError(e);
  }
}

export async function launchLiveCampaign(
  windowMinutes: number,
  body: CampaignLaunchBody,
): Promise<CampaignSummary | null> {
  const result = await postRequest(
    `${BASE}/analytics/live-users/campaign?minutes=${windowMinutes}`,
    body,
  );
  if (!result) return null;
  return result as CampaignSummary;
}

function parseSystemNotificationError(e: unknown): SystemNotificationResult {
  const anyE = e as { response?: { data?: { code?: string; message?: string } } };
  const data = anyE?.response?.data;
  if (data && typeof data.code === 'string' && typeof data.message === 'string') {
    return { ok: false, error: { code: data.code, message: data.message } };
  }
  return {
    ok: false,
    error: { code: 'unknown', message: 'Notification failed — check connection or admin permissions.' },
  };
}

function parseMutationError(e: unknown): UserMutationResult {
  // Avoid a hard import of axios here — apiClient already imports it.
  // The interceptor preserves the response object on errors.
  const anyE = e as { response?: { data?: { code?: string; message?: string }; status?: number } };
  const data = anyE?.response?.data;
  if (data && typeof data.code === 'string' && typeof data.message === 'string') {
    return { ok: false, error: { code: data.code, message: data.message } };
  }
  return {
    ok: false,
    error: { code: 'unknown', message: 'Request failed — check your connection or admin permissions.' },
  };
}
